import { Center, Spinner, Text, VStack } from "@chakra-ui/react"
import React, { useCallback, useEffect, useState } from "react"
import { AlertTargetGroupsByKeywordQueryVariables, useAlertTargetGroupsByKeywordQuery } from "../../graphql"
import { AlertTargetGroupsList } from "./AlertTargetGroupsList"

export type AlertTargetGroupsByKeywordProps = {
	keyword: string
}

export const AlertTargetGroupsByKeyword: React.FC<AlertTargetGroupsByKeywordProps> = ({ keyword }) => {
	const [filter, setFilter] = useState({ keyword })

	const [pagination, setPagination] = useState<AlertTargetGroupsByKeywordQueryVariables["pagination"]>({ limit: 20, page: 1 })

	const [{ data, fetching, error }] = useAlertTargetGroupsByKeywordQuery({
		variables: { ...filter, pagination },
	})

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setFilter({ keyword })
		}, 1000)

		return () => {
			clearTimeout(timeoutId)
		}
	}, [keyword])

	const nextPage = useCallback(() => {
		if (fetching) return

		if (data?.alertTargetGroupsByKeyword.hasNextPage) {
			setPagination((prev) => ({
				...prev,
				page: (prev.page || 0) + 1,
			}))
		}
	}, [data, fetching])

	return (
		<VStack w="full" maxW="2xl" align="stretch">
			{fetching && !data?.alertTargetGroupsByKeyword ? (
				<Center w="full" py="4">
					<VStack w="full" color="grayscale.label">
						<Text fontSize="sm">Loading alert target groups</Text>
						<Spinner size="sm" />
					</VStack>
				</Center>
			) : error ? (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						{error.message.replace("[GraphQL] ", "")}
					</Text>
				</Center>
			) : data?.alertTargetGroupsByKeyword.alertTargetGroups && data.alertTargetGroupsByKeyword.alertTargetGroups.length ? (
				<VStack w="full" align="stretch">
					<Text>
						Showing {data.alertTargetGroupsByKeyword.alertTargetGroups.length || 0} alert target group
						{(data.alertTargetGroupsByKeyword.alertTargetGroups.length || 0) > 1 ? "s" : ""}
					</Text>

					<AlertTargetGroupsList alertTargetGroups={data.alertTargetGroupsByKeyword.alertTargetGroups} loadMore={nextPage} />

					{fetching && (
						<Center w="full" py="4">
							<VStack w="full" color="grayscale.label">
								<Text fontSize="sm">Loading more alert target groups</Text>
								<Spinner size="sm" />
							</VStack>
						</Center>
					)}
				</VStack>
			) : (
				<Center py="4">
					<Text fontSize="sm" fontWeight="semibold" color="error.500">
						Couldn&apos;t find any alert target groups.
					</Text>
				</Center>
			)}
		</VStack>
	)
}
